import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const RecommendationFilters = ({ 
  onFiltersChange = () => {},
  totalRecommendations = 0,
  className = ""
}) => {
  const [filters, setFilters] = useState({
    sortBy: 'priority',
    priority: 'all',
    priceRange: 'all',
    difficulty: 'all',
    compatibility: 'all'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const sortOptions = [
    { value: 'priority', label: 'Priority (High to Low)' },
    { value: 'cost-low', label: 'Cost (Low to High)' },
    { value: 'cost-high', label: 'Cost (High to Low)' },
    { value: 'difficulty', label: 'Installation Difficulty' },
    { value: 'compatibility', label: 'Best Compatibility' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const priceRangeOptions = [
    { value: 'all', label: 'All Price Ranges' },
    { value: 'under-100', label: 'Under $100' },
    { value: '100-500', label: '$100 - $500' },
    { value: '500-1000', label: '$500 - $1,000' },
    { value: 'over-1000', label: 'Over $1,000' }
  ];

  const difficultyOptions = [
    { value: 'all', label: 'All Difficulties' },
    { value: 'easy', label: 'Easy Installation' },
    { value: 'medium', label: 'Medium Difficulty' },
    { value: 'hard', label: 'Professional Required' }
  ];

  const compatibilityOptions = [
    { value: 'all', label: 'All Compatibility' },
    { value: 'perfect', label: 'Perfect Match' },
    { value: 'good', label: 'Good Match' },
    { value: 'adapter', label: 'Requires Adapter' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const resetFilters = () => {
    const resetFilters = {
      sortBy: 'priority',
      priority: 'all',
      priceRange: 'all',
      difficulty: 'all',
      compatibility: 'all'
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.filter(value => value !== 'all' && value !== 'priority')?.length;
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground">Upgrade Recommendations</h3>
            <p className="text-sm text-muted-foreground">
              {totalRecommendations} recommendations found
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {getActiveFilterCount() > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                iconName="X"
                iconPosition="left"
              >
                Clear ({getActiveFilterCount()})
              </Button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="lg:hidden p-2 hover:bg-muted rounded-full transition-colors interactive-scale"
              aria-label="Toggle filters"
            >
              <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
            </button>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className={`p-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Sort By */}
          <Select
            label="Sort By"
            options={sortOptions}
            value={filters?.sortBy}
            onChange={(value) => handleFilterChange('sortBy', value)}
          />

          {/* Priority Filter */}
          <Select
            label="Priority"
            options={priorityOptions}
            value={filters?.priority}
            onChange={(value) => handleFilterChange('priority', value)}
          />

          {/* Price Range Filter */}
          <Select
            label="Price Range"
            options={priceRangeOptions}
            value={filters?.priceRange}
            onChange={(value) => handleFilterChange('priceRange', value)}
          />

          {/* Difficulty Filter */}
          <Select
            label="Installation"
            options={difficultyOptions}
            value={filters?.difficulty}
            onChange={(value) => handleFilterChange('difficulty', value)}
          />

          {/* Compatibility Filter */}
          <Select
            label="Compatibility"
            options={compatibilityOptions}
            value={filters?.compatibility}
            onChange={(value) => handleFilterChange('compatibility', value)}
          />
        </div>

        {/* Quick Filter Buttons */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
          <button
            onClick={() => handleFilterChange('priority', 'high')}
            className={`px-3 py-1 rounded-full text-sm transition-colors interactive-scale ${
              filters?.priority === 'high' ?'bg-error text-error-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            High Priority Only
          </button>
          <button
            onClick={() => handleFilterChange('difficulty', 'easy')}
            className={`px-3 py-1 rounded-full text-sm transition-colors interactive-scale ${
              filters?.difficulty === 'easy' ?'bg-success text-success-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Easy Install
          </button>
          <button
            onClick={() => handleFilterChange('priceRange', 'under-100')}
            className={`px-3 py-1 rounded-full text-sm transition-colors interactive-scale ${
              filters?.priceRange === 'under-100' ?'bg-accent text-accent-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Budget Friendly
          </button>
          <button
            onClick={() => handleFilterChange('compatibility', 'perfect')}
            className={`px-3 py-1 rounded-full text-sm transition-colors interactive-scale ${
              filters?.compatibility === 'perfect' ?'bg-accent text-accent-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Perfect Match
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecommendationFilters;